import type { FC } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { CarParts } from '@dhl/illustrations'

import { Box, Button, Sx, Typography } from '@dhl/theme'
import { selectIsLoggedIn, selectIsParcelShop, selectParcelShopServices } from '~/account/model/selectors'
import { ReduxState } from '~/redux/createRootReducer'
import { logout } from '~/account/model/actions'
import { ParcelShopService } from '~/account/model/reducer'

const classes: Record<string, Sx> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '400px',
    margin: 'auto',
    alignItems: 'center',
    textAlign: 'center',
  },
  illustration: {
    width: '255px',
    height: '170px',
  },
}

type Props = {
  isLoggedIn: boolean | null
  isParcelShop: boolean
  parcelShopServices: ParcelShopService[]
}

export const withParcelShopService = (parcelShopService: ParcelShopService) => (WrappedComponent: FC<Props>) => {
  const WithParcelShopService = (props: Props) => {
    const { isParcelShop, parcelShopServices: availableServices } = props
    const dispatch = useDispatch()
    const { t } = useTranslation()

    if (isParcelShop && !availableServices.includes(parcelShopService)) {
      return (
        <Box sx={classes.root}>
          <Box mt={4}>
            <CarParts sx={classes.illustration} />
          </Box>
          <Box mt={1}>
            <Typography variant="h4">{t('otc_not_allowed_title')}</Typography>
          </Box>
          <Box mt={1}>
            <Typography>{t('otc_not_allowed_text')}</Typography>
          </Box>
          <Box mt={1}>
            <Button variant="contained" color="primary" onClick={() => dispatch(logout())} fullWidth>
              {t('logout')}
            </Button>
          </Box>
        </Box>
      )
    }

    return <WrappedComponent {...props} />
  }

  return connect((state: ReduxState) => ({
    ...selectIsLoggedIn(state),
    ...selectIsParcelShop(state),
    ...selectParcelShopServices(state),
  }))(WithParcelShopService)
}
